/**
 * localStorage for the acute-case desk session (ZUB calendar drawer).
 *
 * Every access is guarded: localStorage throws SecurityError when storage is
 * disabled (private browsing, enterprise policy), and a drawer convenience
 * feature must never white-screen the calendar — the same reasoning as the
 * sessionStorage helpers in routes/calendar.tsx. With storage unavailable the
 * drawer simply asks for the password every time.
 */

export type DeskRole = 'ordinace' | 'recepce'

export interface StoredDeskSession {
	token: string
	role: DeskRole
	/** ISO string; a session past this is treated as absent. */
	expiresAt: string
}

const DESK_SESSION_KEY = 'dentora_desk_session'

function isRole(value: unknown): value is DeskRole {
	return value === 'ordinace' || value === 'recepce'
}

/**
 * The stored session, or null when there is none, it is unreadable, or it has
 * expired. Expiry is checked on read so a terminal left open overnight does
 * not look logged in the next morning.
 */
export function readDeskSession(): StoredDeskSession | null {
	let raw: string | null
	try {
		raw = localStorage.getItem(DESK_SESSION_KEY)
	} catch {
		return null
	}
	if (!raw) return null

	try {
		const parsed = JSON.parse(raw) as Partial<StoredDeskSession>
		if (
			typeof parsed?.token !== 'string' ||
			typeof parsed?.expiresAt !== 'string' ||
			!isRole(parsed?.role)
		) {
			return null
		}
		if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
			clearDeskSession()
			return null
		}
		return parsed as StoredDeskSession
	} catch {
		// Corrupt entry — drop it rather than re-reading it on every render
		clearDeskSession()
		return null
	}
}

export function writeDeskSession(session: StoredDeskSession): void {
	try {
		localStorage.setItem(DESK_SESSION_KEY, JSON.stringify(session))
	} catch {
		// Storage unavailable — the session just won't survive a reload.
	}
}

export function clearDeskSession(): void {
	try {
		localStorage.removeItem(DESK_SESSION_KEY)
	} catch {
		// Nothing to do: there was nothing readable to clear either.
	}
}

/** Bare token for the X-Desk-Session header; null when not logged in. */
export function readDeskToken(): string | null {
	return readDeskSession()?.token ?? null
}
